import React, { useEffect, useRef, useState } from "react"
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, FormLabel, IconButton, Paper, Radio,
  RadioGroup, Stack, Switch, TextField, Typography,
} from "@mui/material"
import LoadingButton from "@mui/lab/LoadingButton"
import SaveIcon from "@mui/icons-material/Save"
import CloseIcon from "@mui/icons-material/Close"
import PasswordIcon from "@mui/icons-material/Password"
import api from "../../../../base/services/ResApi"
import AuthApi from "../../../../base/services/AuthApi"
import StorageService from "../../../../base/services/StorageService"
import OrgsDropdown from "../../../components/orgsDropdown/OrgsDropdown"
import IconUpload from "../../../components/iconUpload/IconUpload"
import base64url from "base64url"
import { JSEncrypt } from "jsencrypt"
import "../../../../styleSheets/base/styles.scss"
import "../styles/grp.styles.scss"

const publicKey = `
    -----BEGIN PUBLIC KEY-----
    MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAsy+1EQqpdOCQM4OaAflU9rQZGzo8TexVbkez7q67egtwZT5BCG5bClBk95paacF3HgMwh4se6ZhttdkiJfr/gGagOqP2FTZKqEaz7d24FSpYyKUVdgZ7eDNgiBbkWVZi83dGqlMGM2wmES3Q614P3JN+BiZ5srvc4q0BXnw8y40MB10KY0dONVuKrywKEKoHCfjIvKJNH3FyYaCwslQC4m94ErDr5OGbO3qPLiJzw7fRoU3WN05OZvGuYkgS0yN2KUKOp/vAyG6h1Mf+bayILTAGJRxyYdLGvL8Y/90NIFNvXtO4IHfY8HO/bymOic/gPE7XU1lHTK1EvrF55a/E3wIDAQAB
    -----END PUBLIC KEY-----`

// Temporarily limited to these supported deployment settings.
const UPDATE_OPTIONS = [
  // { value: "REGULAR_UPDATE", label: "Regular Updates" },
  { value: "SILENT_UPDATE", label: "Silent Update" },
  // { value: "SCHEDULE_UPDATE", label: "Schedule Update" },
]
const SETTLEMENT_OPTIONS = [
  { value: "SKIP_SETTLEMENT", label: "Skip Settlement" },
  // { value: "AUTO_SETTLEMENT", label: "Auto Settlement" },
  // { value: "NEXT_SETTLEMENT", label: "Update After Next Settlement" },
]
// When restoring SCHEDULE_UPDATE, restore DateTimeSelector, schedule-date state,
// and the schedUpdateTime payload field alongside its option.

const initialForm = (group) => ({
  grpName: group?.groupName ?? "",
  icon: group?.icon || "Groups2",
  description: group?.description ?? "",
  deployStgy: "SILENT_UPDATE",
  stlmntSetting: "SKIP_SETTLEMENT",
  clearApp: !!group?.clearAppStatus,
})

const changedFields = (form, group) => {
  const original = {
    grpName: group?.groupName ?? "", icon: group?.icon || "Groups2",
    description: group?.description ?? "", deployStgy: group?.deployStgy,
    stlmntSetting: group?.settlementStng, clearApp: !!group?.clearAppStatus,
  }
  return Object.fromEntries(Object.entries(form).filter(([key, value]) => value !== original[key]))
}

const Section = ({ title, children }) => (
  <Paper component="section" aria-label={title} variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, borderColor: "#dce7e7" }}>
    <Typography component="h3" variant="subtitle1" fontWeight={600} color="#234d50" sx={{ mb: 2 }}>{title}</Typography>
    {children}
  </Paper>
)

const DeploymentSetting = ({ label, name, options, value, onChange, description }) => (
  <FormControl component="fieldset" sx={{ minWidth: 0, p: 2, bgcolor: "#f0f7f6", border: "1px solid #dce7e7", borderRadius: 2 }}>
    <FormLabel component="legend" sx={{ fontSize: 14, color: "#466b6c" }}>{label}</FormLabel>
    <RadioGroup name={name} value={value} onChange={onChange} aria-label={label}>
      {options.map((option) => <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />)}
    </RadioGroup>
    <Typography variant="body2" color="text.secondary">{description}</Typography>
  </FormControl>
)

const EditGroupsModal = ({ open, orgCode, onClose, setUpdateData, grpData, grpId }) => {
  const [form, setForm] = useState(() => initialForm(grpData))
  const [password, setPassword] = useState("")
  const [verified, setVerified] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [passwordError, setPasswordError] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const session = useRef(0)
  const pending = useRef(false)
  const verificationPending = useRef(false)
  const closeTimer = useRef(null)

  useEffect(() => {
    setForm(initialForm(grpData))
    setPassword("")
    setVerified(false)
    setVerifying(false)
    setPasswordError("")
    setSaving(false)
    setError("")
    setSuccess("")
    pending.current = false
    verificationPending.current = false
    return () => { session.current += 1; clearTimeout(closeTimer.current) }
  }, [open, grpId, grpData])

  const handleChange = (event) => {
    const { name, value } = event.target
    setForm((previous) => ({ ...previous, [name]: value }))
  }

  const handleOnClose = () => {
    if (saving) return
    session.current += 1
    onClose()
  }

  const verifyPassword = async () => {
    if (!password || verificationPending.current || saving || verified) return
    const currentSession = session.current
    verificationPending.current = true
    setVerifying(true)
    setPasswordError("")
    try {
      const encrypt = new JSEncrypt()
      encrypt.setPublicKey(publicKey)
      const encrypted = encrypt.encrypt(password)
      if (!encrypted) throw new Error("Encryption failed")
      const response = await AuthApi.get("/users/confirm/" + StorageService.getUsername(), {
        headers: { "Content-Type": "application/json", password: base64url.toBase64(encrypted) },
      })
      if (currentSession !== session.current) return
      if (response.data === true) {
        setVerified(true)
        setPassword("")
      } else setPasswordError("Incorrect password. Please try again.")
    } catch (err) {
      if (currentSession === session.current) setPasswordError("Unable to verify your password. Please try again.")
    } finally {
      if (currentSession === session.current) {
        verificationPending.current = false
        setVerifying(false)
      }
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (pending.current || verificationPending.current) return
    if (!form.grpName.trim()) { setError("Enter a group name."); return }
    if (form.clearApp !== !!grpData?.clearAppStatus && !verified) {
      setError("Verify your password before changing Clear App Data.")
      return
    }
    const currentSession = session.current
    pending.current = true
    setSaving(true)
    setError("")
    try {
      const response = await api.put("/groups", { grpId, ...changedFields(form, grpData) })
      if (currentSession !== session.current) return
      setSuccess(response.data?.description || "Group updated successfully.")
      closeTimer.current = setTimeout(() => {
        onClose()
        setUpdateData(true)
        pending.current = false
        setSaving(false)
      }, 4000)
    } catch (err) {
      if (currentSession === session.current) {
        setError(err?.response?.data?.description || "Unable to update the group. Please try again.")
        pending.current = false
        setSaving(false)
      }
    }
  }

  return (
    <Dialog fullWidth maxWidth={false} open={open} PaperProps={{ style: { borderRadius: "10px", width: "912px", margin: 0 }, sx: { maxWidth: "calc(100% - 32px)" } }}>
      <DialogTitle className="grpFormTitle" sx={{ position: "relative", width: "auto", flexShrink: 0 }}>
        Update Group
        <IconButton aria-label="cancel" onClick={handleOnClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f8fa" }}>
          <Stack spacing={2.5}>
            {success && <Alert severity="success">{success}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <Box component="fieldset" disabled={saving} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
              <Stack spacing={2.5}>
                <Section title="Group details">
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ sm: "center" }}>
                    <Box sx={{ width: 100, flexShrink: 0, "& .imageUploader": { mb: 0 }, "& .p-image": { bottom: 28, height: 0 } }}>
                      <IconUpload tooltip="Select Group Icon" grpIcon={form.icon}
                        setgrpIcon={(icon) => setForm((previous) => ({ ...previous, icon }))} onChange={handleChange} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                      <TextField name="grpName" value={form.grpName} label="Group Name" required fullWidth color="secondary" onChange={handleChange} />
                      <Box sx={{ minWidth: 0, minHeight: 58, "& .MuiFormControl-root": { minWidth: 0, width: "100%" } }}>
                        <OrgsDropdown orgCode={orgCode || ""} onChange={handleChange} isRequired isOrgDisabled />
                      </Box>
                    </Box>
                  </Stack>
                  <TextField fullWidth name="description" value={form.description} label="Description" multiline rows={2} color="secondary" onChange={handleChange} sx={{ mt: 3 }} />
                </Section>
                <Section title="Deployment strategy">
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                    <DeploymentSetting label="Software Update for terminals" name="deployStgy" options={UPDATE_OPTIONS} value={form.deployStgy} onChange={handleChange} description="Software updates automatically without terminal user confirmation." />
                    <DeploymentSetting label="Settlement setting" name="stlmntSetting" options={SETTLEMENT_OPTIONS} value={form.stlmntSetting} onChange={handleChange} description="Software updates proceed without waiting for settlement." />
                  </Box>
                </Section>
                <Section title="Clear App Data">
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="body2" color="text.secondary">Clear terminal application data during the update. Verify your password to change this setting.</Typography>
                    <Switch color="error" checked={form.clearApp} disabled={!verified || saving} inputProps={{ "aria-label": "Clear App Data" }} onChange={(event) => setForm((previous) => ({ ...previous, clearApp: event.target.checked }))} />
                  </Stack>
                  {verified ? <Alert severity="success" sx={{ mt: 2 }}>Password verified. You can now change Clear App Data.</Alert> :
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }} sx={{ mt: 2 }}>
                      <TextField label="Confirm Password" type="password" autoComplete="current-password" value={password} size="small" fullWidth disabled={verifying} error={!!passwordError} helperText={passwordError || "Your account password is required only for this setting."}
                        onChange={(event) => { setPassword(event.target.value); setPasswordError("") }}
                        onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); verifyPassword() } }} />
                      <LoadingButton loading={verifying} disabled={!password || saving} startIcon={<PasswordIcon />} variant="contained" color="success" onClick={verifyPassword} sx={{ flexShrink: 0, textTransform: "none" }}>Verify password</LoadingButton>
                    </Stack>}
                </Section>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: "1.25rem" }}>
          <Button onClick={handleOnClose} variant="outlined" color="error">Cancel</Button>
          <LoadingButton type="submit" color="success" loading={saving} disabled={verifying} variant="contained" loadingPosition="start" startIcon={<SaveIcon />}>Save</LoadingButton>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default EditGroupsModal
